import { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  faSearch,
  faTrashCan,
} from '@fortawesome/free-solid-svg-icons';
import { ApiEndpoints, FrontendRoutes } from '@helpers/Constants/Navigation';
import { Axios } from '@helpers/ApiService';
import { AxiosError } from 'axios';
import {
  errorMessage,
  onboardingStepId,
  ProductType,
  responseTexts,
  searchConfig,
  WeightType,
} from '@helpers/Constants';
import { useToast } from '@contexts/Toast';
import { UseConfirmationToast } from '@contexts/ConfirmationToast';
import { Product } from '@helpers/Models';
import { useLocalStorage } from '@hooks/useLocalStorage';
import { useUnsavedChangesPrompt } from '@hooks/useNavigationBlocker';

const ProductList: React.FC = () => {
  const { updateOnboardingStep } = useLocalStorage();
  const [products, setProducts] = useState<Product[]>([]);
  const [initialProducts, setInitialProducts] = useState<Product[]>([]);
  const [allDeletedProducts, setAllDeletedProducts] = useState<Product[]>([]);

  const [errors, setErrors] = useState<{
    [key: number]: {
      name?: string;
      minimumQuantity?: string;
      weightType2?: string;
      weightType1?: string;
      taxableFlag?: string;
      priceType1?: string;
      priceType2?: string;
      duplicate?: string;
    };
  }>({});
  const { showToastMessage } = useToast();
  const { showConfirmationToast } = UseConfirmationToast();
  const navigate = useNavigate();
  const [searchKey, setSearchKey] = useState('');
  const [debouncedSearchKey, setDebouncedSearchKey] = useState('');
  const location = useLocation();
  const fromSettings = location.state?.fromSettings;
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  useUnsavedChangesPrompt(hasUnsavedChanges, undefined, 'Leave Without Saving');

  const normalizeFlag = (value: unknown, fallback = 0): number => {
    if (value === undefined || value === null || value === '') return fallback;
    return Number(value) === 1 ? 1 : 0;
  };

  const normalizeProduct = (
    product: Partial<Product> & { deletedAt?: unknown },
    fallback?: Product,
  ): Product => ({
    id: Number(product.id ?? fallback?.id ?? Date.now()),
    name: String(product.name ?? fallback?.name ?? ''),
    availableFlag: normalizeFlag(product.availableFlag, fallback?.availableFlag ?? 1),
    upchargeIngredientsFlag: normalizeFlag(
      product.upchargeIngredientsFlag,
      fallback?.upchargeIngredientsFlag ?? 0,
    ),
    allowFatContentFlag: normalizeFlag(
      product.allowFatContentFlag,
      fallback?.allowFatContentFlag ?? 0,
    ),
    allowAddOnFlag: normalizeFlag(
      product.allowAddOnFlag,
      fallback?.allowAddOnFlag ?? 0,   // ✅ Default to 0
    ),
    price: Number(product.price ?? fallback?.price ?? 0),
    minimumQuantity: Number(product.minimumQuantity ?? fallback?.minimumQuantity ?? 0),
    weight: Number(product.weight ?? fallback?.weight ?? 0),
    taxableFlag: normalizeFlag(product.taxableFlag, fallback?.taxableFlag ?? 0),
    productType:
      Number(product.productType ?? fallback?.productType ?? ProductType.WEIGHT) ===
        ProductType.QUANTITY
        ? ProductType.QUANTITY
        : ProductType.WEIGHT,
  });

  // --- Helper to detect changes between current products and initial list ---
  const hasProductChanges = (productsList: Product[] = products): boolean => {
    if (initialProducts.length !== productsList.length) return true;
    return productsList.some((product, index) => {
      const initial = initialProducts[index];
      if (!initial) return true;
      return (
        product.name !== initial.name ||
        product.price !== initial.price ||
        product.weight !== initial.weight ||
        product.productType !== initial.productType ||
        product.minimumQuantity !== initial.minimumQuantity ||
        product.taxableFlag !== initial.taxableFlag ||
        product.allowAddOnFlag !== initial.allowAddOnFlag ||
        product.availableFlag !== initial.availableFlag ||
        product.upchargeIngredientsFlag !== initial.upchargeIngredientsFlag ||
        product.allowFatContentFlag !== initial.allowFatContentFlag
      );
    });
  };

  // --- Effects ---
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearchKey(searchKey);
    }, searchConfig.SEARCH_DELAY);
    return () => clearTimeout(handler);
  }, [searchKey]);

  useEffect(() => {
    fetchProducts(debouncedSearchKey);
  }, [debouncedSearchKey]);

  useEffect(() => {
    fetchAllDeletedProducts();
  }, []);

  // --- Data fetching ---
  const fetchAllDeletedProducts = async () => {
    try {
      const response = await Axios.post(ApiEndpoints.FETCH_PRODUCTS, {
        searchKey: '',
      });
      const all = response.data.data.products;
      const deleted = all.filter((p: any) => p.deletedAt);
      setAllDeletedProducts(deleted);
    } catch (error) {
      // silently fail
    }
  };

  const fetchProducts = async (searchKey: string): Promise<void> => {
    try {
      const response = await Axios.post(ApiEndpoints.FETCH_PRODUCTS, {
        searchKey,
      });
      const fetched = response.data.data.products as Array<
        Partial<Product> & { deletedAt?: unknown }
      >;
      const active: Product[] = fetched
        .filter((p) => !p.deletedAt)
        .map((p) => normalizeProduct(p));
      setProducts(active);
      if (initialProducts.length === 0 && !searchKey) {
        setInitialProducts(JSON.parse(JSON.stringify(active)));
        setHasUnsavedChanges(false);
      } else {
        setHasUnsavedChanges(hasProductChanges(active));
      }
    } catch (error) {
      showToastMessage(errorMessage.FETCH_ERROR, 'error');
    }
  };

  // --- Product updates ---
  const updateProduct = (id: number, field: string, value: string | number | boolean) => {
    setProducts((prev) =>
      prev.map((product) =>
        product.id === id ? { ...product, [field]: value } : product
      )
    );
    setHasUnsavedChanges(true);
  };

  // Recalculate hasUnsavedChanges whenever products change (except during initial load)
  useEffect(() => {
    if (initialProducts.length > 0) {
      setHasUnsavedChanges(hasProductChanges());
    }
  }, [products, initialProducts]);

  // Helper to clear a specific field error
  const clearFieldError = (productId: number, fieldName: string) => {
    setErrors((prev) => {
      const newErrors = { ...prev };
      if (!newErrors[productId]) return newErrors;

      let errorKey: string | null = null;
      const product = products.find(p => p.id === productId);

      switch (fieldName) {
        case 'name':
          errorKey = 'name';
          // Also clear duplicate error when name changes
          if (newErrors[productId].duplicate) {
            delete newErrors[productId].duplicate;
          }
          break;
        case 'price':
          errorKey = product?.productType === ProductType.WEIGHT ? 'priceType1' : 'priceType2';
          break;
        case 'weight':
          errorKey = product?.productType === ProductType.WEIGHT ? 'weightType1' : 'weightType2';
          break;
        case 'minimumQuantity':
          errorKey = 'minimumQuantity';
          break;
        case 'taxableFlag':
          errorKey = 'taxableFlag';
          break;
        // For checkboxes we don't clear errors (they don't have field-specific errors)
        default:
          return newErrors;
      }

      if (errorKey && (newErrors[productId] as any)[errorKey]) {
        delete (newErrors[productId] as any)[errorKey];
        if (Object.keys(newErrors[productId]).length === 0) {
          delete newErrors[productId];
        }
      }
      return newErrors;
    });
  };

  const handleInputChange = (id: number, e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;

    if (type === 'checkbox') {
      // Special handling for Allow Add-on: if unchecking, also uncheck Charge Add-ons
      if (name === 'allowAddOnFlag' && !checked) {
        // Uncheck Charge Add-ons first
        updateProduct(id, 'upchargeIngredientsFlag', 0);
      }

      if (name === 'upchargeIngredientsFlag') {
        const product = products.find(p => p.id === id);
        if (product && product.allowAddOnFlag !== 1) {
          showToastMessage('Charge Add-ons can only be enabled when Allow Add-on is enabled', 'error');
          return;
        }
      }

      updateProduct(id, name, checked ? 1 : 0);
      clearFieldError(id, name);
      return;
    }

    // For text/numeric inputs
    if (name === 'price' || name === 'weight' || name === 'minimumQuantity') {
      if (value === '') {
        updateProduct(id, name, '' as any);
        clearFieldError(id, name);
      } else if (/^\d*\.?\d*$/.test(value)) {
        updateProduct(id, name, Number(value));
        clearFieldError(id, name);
      }
      return;
    }

    // For name (text)
    updateProduct(id, name, value);
    clearFieldError(id, name);
  };

  const handleProductTypeChange = (id: number, type: number) => {
    setProducts((prev) =>
      prev.map((product) =>
        product.id === id
          ? {
            ...product,
            productType: type,
            weight: 0,
            price: 0,
            minimumQuantity: 0,
          }
          : product
      )
    );
    setHasUnsavedChanges(true);
    // Clear all errors for this product because field keys change
    setErrors((prev) => {
      const newErrors = { ...prev };
      delete newErrors[id];
      return newErrors;
    });
  };

  const getInputDisplayValue = (value: number | string | undefined) => {
    if (value === undefined || value === null) return '';
    return String(value);
  };

  // --- Delete operations ---
  const performDeleteProduct = async (id: number): Promise<void> => {
    try {
      if (id > 10000) {
        setProducts((prev) => {
          const newProducts = prev.filter((p) => p.id !== id);
          setHasUnsavedChanges(hasProductChanges(newProducts));
          return newProducts;
        });
        showToastMessage('Product removed successfully', 'success');
      } else {
        await Axios.delete(ApiEndpoints.DELETE_PRODUCTS, {
          data: { ids: [id] },
        });
        setProducts((prev) => prev.filter((p) => p.id !== id));
        showToastMessage(responseTexts.PRODUCT_DELETE_SUCCESS, 'success');
        fetchAllDeletedProducts();
      }
    } catch (error) {
      if (error instanceof AxiosError) {
        const msg = error.response?.data?.message ?? errorMessage.DEFAULT_ERROR_MESSAGE;
        showToastMessage(msg, 'error');
      }
    }
  };

  const deleteProduct = (id: number): void => {
    showConfirmationToast(
      'Are you sure you want to delete this item?',
      'Delete Confirmation',
      'delete',
      () => performDeleteProduct(id),
      'Delete',
      undefined,
      'Cancel'
    );
  };

  // --- Validation ---
  const validateAllProducts = (): boolean => {
    let isValid = true;
    const newErrors: { [key: number]: any } = {};

    const nameMap = new Map<string, number[]>();
    products.forEach((product) => {
      const err: any = {};
      const trimmedName = product.name?.trim() || '';
      if (!trimmedName) {
        err.name = errorMessage.PRODUCT_NAME_REQUIRED;
        isValid = false;
      } else {
        const key = trimmedName.toLowerCase();
        if (!nameMap.has(key)) {
          nameMap.set(key, [product.id]);
        } else {
          nameMap.get(key)!.push(product.id);
        }
      }

      // Price validation: allow 0, but not negative
      if (product.price < 0) {
        err[product.productType === 1 ? 'priceType1' : 'priceType2'] =
          'Price cannot be negative';
        isValid = false;
      }

      // Weight validation: required for weight product, optional for quantity
      if (product.productType === ProductType.WEIGHT) {
        const weightStr = String(product.weight ?? '').trim();
        if (weightStr === '') {
          err.weightType1 = 'Min Order Weight is required';
          isValid = false;
        } else if (Number(weightStr) < 0) {
          err.weightType1 = 'Weight cannot be negative';
          isValid = false;
        }
      } else {
        // Quantity product: weight is optional, only validate if present
        const weightStr = String(product.weight ?? '').trim();
        if (weightStr !== '') {
          if (Number(weightStr) < 0) {
            err.weightType2 = 'Weight cannot be negative';
            isValid = false;
          }
        }
        // Minimum quantity validation: required for quantity product
        const minQtyStr = String(product.minimumQuantity ?? '').trim();
        if (minQtyStr === '') {
          err.minimumQuantity = 'Min Order Quantity is required';
          isValid = false;
        } else if (Number(minQtyStr) <= 0) {
          err.minimumQuantity = errorMessage.PRODUCT_MINQUANTITY_REQUIRED;
          isValid = false;
        }
      }

      if (Object.keys(err).length) newErrors[product.id] = err;
    });

    nameMap.forEach((ids: number[], _name: string) => {
      if (ids.length > 1) {
        ids.forEach((id: number) => {
          if (!newErrors[id]) newErrors[id] = {};
          newErrors[id].duplicate = 'Product name must be unique';
        });
        isValid = false;
      }
    });

    setErrors(newErrors);
    if (!isValid) showToastMessage(errorMessage.FORM_REQUIRED, 'error');
    return isValid;
  };

  // --- Save ---
  const handleSave = async (): Promise<void> => {
    if (!validateAllProducts()) return;
    if (!hasUnsavedChanges) return;

    let deletedProducts = allDeletedProducts;
    if (deletedProducts.length === 0) {
      try {
        const response = await Axios.post(ApiEndpoints.FETCH_PRODUCTS, { searchKey: '' });
        const all = response.data.data.products;
        deletedProducts = all.filter((p: any) => p.deletedAt);
        setAllDeletedProducts(deletedProducts);
      } catch (error) {
        showToastMessage('Could not fetch deleted products for restoration', 'error');
        return;
      }
    }

    const payload: any[] = [];
    const restoreIds: number[] = [];
    let updatedCount = 0;

    for (const product of products) {
      let id = product.id;
      if (product.id > 10000) {
        const matched = deletedProducts.find(
          (d) => d.name.trim().toLowerCase() === product.name.trim().toLowerCase()
        );
        if (matched) {
          id = matched.id;
          restoreIds.push(matched.id);
        }
        // else it's a brand new product (will have id = -1 in payload)
      } else {
        // Check if this product exists in initial list with same id and any field changed
        const initial = initialProducts.find(p => p.id === product.id);
        if (initial) {
          const hasChanged = (
            product.name !== initial.name ||
            product.price !== initial.price ||
            product.weight !== initial.weight ||
            product.productType !== initial.productType ||
            product.minimumQuantity !== initial.minimumQuantity ||
            product.taxableFlag !== initial.taxableFlag ||
            product.allowAddOnFlag !== initial.allowAddOnFlag ||
            product.availableFlag !== initial.availableFlag ||
            product.upchargeIngredientsFlag !== initial.upchargeIngredientsFlag ||
            product.allowFatContentFlag !== initial.allowFatContentFlag
          );
          if (hasChanged) updatedCount++;
        }
      }

      const allowAddOnFlag = normalizeFlag(product.allowAddOnFlag, 0);
      const upchargeIngredientsFlag =
        allowAddOnFlag === 1
          ? normalizeFlag(product.upchargeIngredientsFlag, 0)
          : 0;

      const data: any = {
        id: id > 10000 ? -1 : id,
        name: product.name,
        productType: Number(product.productType) || ProductType.WEIGHT,
        weight: Number(product.weight) || 0,
        price: Number(product.price) || 0,
        taxableFlag: normalizeFlag(product.taxableFlag, 0),
        allowAddOnFlag,
        upchargeIngredientsFlag,
        allowFatContentFlag: normalizeFlag(product.allowFatContentFlag, 0),
        availableFlag: normalizeFlag(product.availableFlag, 0),
      };

      if (product.productType === 1) {
        delete data.minimumQuantity;
      } else {
        data.minimumQuantity = Number(product.minimumQuantity) || 0;
      }

      payload.push(data);
    }

    if (payload.length === 0 && restoreIds.length === 0) {
      showToastMessage(errorMessage.PRODUCT_LIST_EMPTY, 'error');
      return;
    }

    try {
      let restoredCount = 0;
      if (restoreIds.length > 0) {
        await Axios.post(ApiEndpoints.RESTORE_PRODUCTS, { ids: restoreIds });
        restoredCount = restoreIds.length;
        setAllDeletedProducts((prev) => prev.filter((d) => !restoreIds.includes(d.id)));
        await fetchProducts(debouncedSearchKey);
        fetchAllDeletedProducts();
      }

      const response = await Axios.post(ApiEndpoints.UPSERT_PRODUCTS, {
        products: payload,
      });
      const saved = (response.data.data as Array<Partial<Product>>).map((item) => {
        const matchedInput = products.find(
          (product) => product.name.trim().toLowerCase() === String(item.name ?? '').trim().toLowerCase(),
        );
        return normalizeProduct(item, matchedInput);
      });
      setProducts(saved);
      updateOnboardingStep(onboardingStepId.PRODUCTS);

      const createdCount = payload.filter((p) => p.id === -1).length;
      const totalAdded = restoredCount + createdCount;
      let msg = '';
      if (totalAdded > 0 && updatedCount > 0) {
        msg = `${totalAdded} product(s) added and ${updatedCount} product(s) updated successfully.`;
      } else if (totalAdded > 0) {
        msg = `${totalAdded} product(s) added successfully.`;
      } else if (updatedCount > 0) {
        msg = `${updatedCount} product(s) updated successfully.`;
      } else {
        msg = 'Products saved successfully.';
      }
      showToastMessage(msg, 'success');

      setInitialProducts(JSON.parse(JSON.stringify(saved)));
      setHasUnsavedChanges(false);
    } catch (error) {
      if (error instanceof AxiosError) {
        const msg = error.response?.data?.message ?? errorMessage.DEFAULT_ERROR_MESSAGE;
        showToastMessage(msg, 'error');
      } else {
        showToastMessage(errorMessage.DEFAULT_ERROR_MESSAGE, 'error');
      }
    }
  };

  // --- Next ---
  const handleNext = () => {
    navigateToAddons();
  };

  const navigateToAddons = () => {
    if (fromSettings) {
      navigate(FrontendRoutes.ADDONS, { state: { fromSettings } });
    } else {
      navigate(FrontendRoutes.ADDONS);
    }
  };

  // ✅ FIX: Ensure allowAddOnFlag is explicitly set to 0 when adding a new product
  const handleAddProduct = (): void => {
    const newProduct: Product = {
      id: Date.now(),
      name: '',
      availableFlag: 1,
      minimumQuantity: 0,
      upchargeIngredientsFlag: 0,
      allowFatContentFlag: 0,
      allowAddOnFlag: 0,   // ✅ Explicitly set to 0 (unchecked)
      price: 0,
      weight: 0,
      productType: 1,
    };
    // For debugging: log the new product to verify defaults
    console.log('New product added:', newProduct);
    setProducts((prev) => [...prev, newProduct]);
    setHasUnsavedChanges(true);
    setTimeout(() => {
      const el = document.getElementById('product-list');
      if (el) el.scrollTop = el.scrollHeight;
    }, 100);
  };

  // --- Render ---
  return (
    <div>
      <div className="flex justify-between items-center">
        <h1 className="text-lg font-semibold mb-4" data-testid="productsTitle">
          Products
        </h1>
        <div className="flex items-center gap-2 mb-6">
          <div className="relative">
            <input
              type="text"
              placeholder="Search..."
              onChange={(e) => setSearchKey(e.target.value)}
              value={searchKey}
              className="border p-2 pl-3 pr-10 w-80 accent-primary focus:outline-primary border-gray-300 rounded"
            />
            <FontAwesomeIcon
              icon={faSearch}
              className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500"
            />
          </div>
        </div>
      </div>
      <div
        className="max-h-[500px] md:max-h-[725px] ipad-pro:max-h-[875px] overflow-y-auto"
        id="product-list"
      >
        {products.map((product) => (
          <div key={product.id} className="flex space-y-2 mb-8">
            <div className="card w-full border border-gray-300 rounded-md min-h-32">
              <div className="card-header bg-gray-200 items-center mx-4 my-2 mt-4 py-2 px-4">
                <div className="flex items-center justify-between">
                  <div className="w-3/5">
                    <input
                      type="text"
                      name="name"
                      value={product.name ?? ''}
                      onChange={(e) => handleInputChange(product.id, e)}
                      placeholder="Product Name"
                      className="w-7/12 font-semibold text-lg rounded-md px-2 py-1 focus:outline-primary"
                    />
                    <div>
                      {errors[product.id]?.name && (
                        <span className="text-red-500 text-sm">
                          {errors[product.id].name}
                        </span>
                      )}
                      {errors[product.id]?.duplicate && (
                        <span className="text-red-500 text-sm block">
                          {errors[product.id].duplicate}
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="w-2/5 flex items-center">
                    <div className="flex items-center min-w-45 max-w-45">
                      <span className="text-sm font-semibold">Available</span>
                      <input
                        type="checkbox"
                        checked={product.availableFlag === 1}
                        onChange={(e) => handleInputChange(product.id, e)}
                        name="availableFlag"
                        className="h-4 w-4 ml-2 accent-primary focus:ring-primary border-gray-300 rounded"
                        aria-label="Available"
                      />
                    </div>

                    <div className="flex items-center justify-center h-full min-w-8 max-w-8">
                      <span className="text-lg font-semibold">|</span>
                    </div>

                    <div className="flex items-center justify-end">
                      <FontAwesomeIcon
                        onClick={() => deleteProduct(product.id)}
                        icon={faTrashCan}
                        className="text-[#4D545C] cursor-pointer hover:text-red-500"
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div className="items-center mx-4 my-2 py-2 px-4">
                <span className="flex items-center text-[#1C1B1F]">
                  Item sold by:
                </span>
                <div className="flex mt-4 w-full justify-between gap-3">
                  <div
                    className={`w-1/2 p-3 rounded-md cursor-pointer transition 
                      ${product.productType === ProductType.WEIGHT
                        ? 'bg-[#F1F8ED] border border-black'
                        : ''
                      }`}
                  >
                    <div className="flex items-center gap-2 mb-3">
                      <input
                        type="radio"
                        name={`productType-${product.id}`}
                        onChange={() => handleProductTypeChange(product.id, ProductType.WEIGHT)}
                        checked={product.productType === ProductType.WEIGHT}
                        className="accent-black"
                      />
                      <p className="font-semibold">Weight</p>
                    </div>
                    <div>
                      <div className="grid grid-cols-2 gap-4 items-center mb-5">
                        <h3 className="font-semibold">
                          Min Order Weight (Lbs)
                        </h3>
                        <div>
                          <input
                            type="number"
                            name="weight"
                            value={
                              product.productType === ProductType.WEIGHT
                                ? getInputDisplayValue(product.weight)
                                : ''
                            }
                            onChange={(e) => handleInputChange(product.id, e)}
                            className="w-28 h-[32px] p-2 border rounded-md text-gray-600 focus:outline-primary text-center"
                          />
                          {errors[product.id]?.weightType1 && (
                            <span className="text-red-500 text-sm block mt-1">
                              {errors[product.id].weightType1}
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-4 items-start mb-5">
                        <h3 className="font-semibold">Price</h3>
                        <div>
                          <div className="flex items-center">
                            <div className="relative">
                              <span className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-500 text-sm">
                                $
                              </span>
                              <input
                                type="number"
                                step="0.01"
                                min="0"
                                name="price"
                                value={
                                  product.productType === ProductType.WEIGHT
                                    ? getInputDisplayValue(product.price)
                                    : ''
                                }
                                onChange={(e) => handleInputChange(product.id, e)}
                                className="w-20 h-[32px] pl-5 pr-2 border rounded-md text-gray-600 focus:outline-primary"
                              />
                            </div>
                            <span className="ml-2">/{WeightType.LBS}</span>
                          </div>
                          {errors[product.id]?.priceType1 && (
                            <span className="text-red-500 text-sm block mt-1">
                              {errors[product.id].priceType1}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  <div
                    className={`w-1/2 p-3 rounded-md cursor-pointer transition 
                      ${product.productType === ProductType.QUANTITY
                        ? 'bg-[#F1F8ED] border border-black'
                        : ''
                      }`}
                  >
                    <div className="flex items-center gap-2 mb-3">
                      <input
                        type="radio"
                        name={`productType-${product.id}`}
                        checked={product.productType === ProductType.QUANTITY}
                        onChange={() => handleProductTypeChange(product.id, ProductType.QUANTITY)}
                        className="accent-black"
                      />
                      <p className="font-semibold">Quantity</p>
                    </div>
                    <div> </div>
                    <div className="grid grid-cols-2 gap-4 items-center mb-5">
                      <h3 className="font-semibold">Min Order Quantity</h3>
                      <div>
                        <input
                          type="number"
                          name="minimumQuantity"
                          value={
                            product.productType === ProductType.QUANTITY
                              ? getInputDisplayValue(product.minimumQuantity)
                              : ''
                          }
                          onChange={(e) => handleInputChange(product.id, e)}
                          className="w-32 h-[32px] p-2 border rounded-md text-gray-600 focus:outline-primary text-center"
                        />
                        {errors[product.id]?.minimumQuantity && (
                          <span className="text-red-500 text-sm block mt-1">
                            {errors[product.id].minimumQuantity}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4 items-center mb-5">
                      <h3 className="font-semibold">
                        Estimated Item Weight (Lbs)
                      </h3>
                      <div>
                        <input
                          type="number"
                          name="weight"
                          value={
                            product.productType === ProductType.QUANTITY
                              ? getInputDisplayValue(product.weight)
                              : ''
                          }
                          onChange={(e) => handleInputChange(product.id, e)}
                          className="w-32 h-[32px] p-2 border rounded-md text-gray-600 focus:outline-primary text-center"
                        />
                        {errors[product.id]?.weightType2 && (
                          <span className="text-red-500 text-sm block mt-1">
                            {errors[product.id].weightType2}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4 items-start mb-5">
                      <h3 className="font-semibold">Price</h3>
                      <div>
                        <div className="flex items-center">
                          <div className="relative">
                            <span className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-500 text-sm">
                              $
                            </span>
                            <input
                              type="number"
                              step="0.01"
                              min="0"
                              name="price"
                              value={
                                product.productType === ProductType.QUANTITY
                                  ? getInputDisplayValue(product.price)
                                  : ''
                              }
                              onChange={(e) => handleInputChange(product.id, e)}
                              className="w-20 h-[32px] pl-5 pr-2 border rounded-md text-gray-600 focus:outline-primary"
                            />
                          </div>
                          <span className="ml-2">Each</span>
                        </div>
                        {errors[product.id]?.priceType2 && (
                          <span className="text-red-500 text-sm block mt-1">
                            {errors[product.id].priceType2}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="items-center mx-4 my-2 py-2 px-4">
                <div className="flex justify-between flex-wrap gap-2">
                  <div className="flex items-center min-w-45 max-w-45">
                    <span className="font-semibold">Taxable Product</span>
                    <input
                      type="checkbox"
                      checked={product.taxableFlag === 1}
                      onChange={(e) => handleInputChange(product.id, e)}
                      name="taxableFlag"
                      className="h-4 w-4 ml-2 accent-primary focus:ring-primary border-gray-300 rounded"
                      aria-label="taxableFlag"
                    />
                  </div>
                  <div className="flex items-center min-w-45 max-w-45">
                    <span className="font-semibold">Allow Fat%</span>
                    <input
                      type="checkbox"
                      checked={product.allowFatContentFlag === 1}
                      onChange={(e) => handleInputChange(product.id, e)}
                      name="allowFatContentFlag"
                      className="h-4 w-4 ml-2 accent-primary focus:ring-primary border-gray-300 rounded"
                      aria-label="allowFatContentFlag"
                    />
                  </div>
                  <div className="flex items-center min-w-45 max-w-45">
                    <span className="font-semibold">Allow Add-on</span>
                    <input
                      type="checkbox"
                      checked={product.allowAddOnFlag === 1}
                      onChange={(e) => handleInputChange(product.id, e)}
                      name="allowAddOnFlag"
                      className="h-4 w-4 ml-2 accent-primary focus:ring-primary border-gray-300 rounded"
                      aria-label="allowAddOnFlag"
                    />
                  </div>
                  <div className="flex items-center min-w-45 max-w-45">
                    <span className="font-semibold">Charge Add-ons</span>
                    <input
                      type="checkbox"
                      checked={product.upchargeIngredientsFlag === 1}
                      onChange={(e) => handleInputChange(product.id, e)}
                      name="upchargeIngredientsFlag"
                      disabled={product.allowAddOnFlag !== 1}
                      className={`h-4 w-4 ml-2 accent-primary focus:ring-primary border-gray-300 rounded ${product.allowAddOnFlag !== 1 ? 'opacity-50 cursor-not-allowed' : ''
                        }`}
                      aria-label="upchargeIngredientsFlag"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {products.length === 0 && (
        <div className="flex items-center justify-center h-full w-full">
          No data available.
        </div>
      )}

      <div className="sticky bottom-0 bg-white border-t border-gray-100 z-10 w-full py-4 mt-3">
        <div className="flex flex-col 2xl:flex-row 2xl:items-center 2xl:justify-end gap-4 w-full">
          <button
            onClick={handleAddProduct}
            type="button"
            className="w-full 2xl:w-[150px] h-10 px-6 flex items-center justify-center space-x-2 border border-primary rounded-md text-[13px] font-semibold text-white bg-primary hover:bg-dark-primary focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary transition-colors duration-200"
          >
            <span>+ Add More</span>
          </button>

          <div className="w-full 2xl:w-auto flex items-center justify-between 2xl:justify-end gap-4">
            <button
              onClick={handleSave}
              type="button"
              disabled={!hasUnsavedChanges}
              className={`w-1/2 2xl:w-[130px] h-10 px-6 border rounded-md text-sm font-medium flex items-center justify-center transition-all duration-200 ${hasUnsavedChanges
                ? 'bg-white text-primary border-primary hover:bg-primary hover:text-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary'
                : 'bg-white text-[#00483480] border-[#00483480] cursor-not-allowed pointer-events-auto'
                }`}
            >
              Save
            </button>

            <button
              onClick={handleNext}
              type="button"
              disabled={hasUnsavedChanges}
              className={`w-1/2 2xl:w-[150px] h-10 px-6 border rounded-md text-sm font-medium flex items-center justify-center space-x-2 transition-all duration-200 ${hasUnsavedChanges
                ? 'bg-gray-300 text-gray-500 cursor-not-allowed border-gray-300'
                : 'bg-primary text-white hover:bg-dark-primary border-transparent focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary'
                }`}
            >
              <span>Next</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductList;